import React from 'react';
import { Box, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import kotilingaSlides, { DEFAULT_SUBTITLE } from '../../config/kotilingaSlides';

// Static QR (encodes http://icctmemphis.org/kotilinga). Shipped locally so it works
// under the app's Content-Security-Policy without an external QR service.
const QR_IMAGE_SRC = '/assets/images/kotilinga/kotilinga-qr.svg';
const AUTOPLAY_MS = 5000;

/**
 * Kotilinga slideshow — auto-playing image carousel with title overlay, QR panel,
 * prev/next arrows and dots. Ported from icct-kiosk-app KotilingaPage slider.
 */
export default function KotilingaSlideshow() {
  const slides = Array.isArray(kotilingaSlides) ? kotilingaSlides : [];
  const slideCount = slides.length;
  const [slideIndex, setSlideIndex] = React.useState(0);
  const [imageDialog, setImageDialog] = React.useState({ open: false, src: '', title: '' });
  const autoPlayRef = React.useRef(null);

  const clearAutoPlay = React.useCallback(() => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
      autoPlayRef.current = null;
    }
  }, []);

  const startAutoPlay = React.useCallback(() => {
    clearAutoPlay();
    if (!slideCount) return;
    autoPlayRef.current = setInterval(() => {
      setSlideIndex((prev) => (prev + 1) % slideCount);
    }, AUTOPLAY_MS);
  }, [clearAutoPlay, slideCount]);

  React.useEffect(() => {
    startAutoPlay();
    return clearAutoPlay;
  }, [startAutoPlay, clearAutoPlay]);

  // Preload images so transitions are smooth.
  React.useEffect(() => {
    slides.forEach((s) => {
      if (!s?.image_src) return;
      const img = new Image();
      img.decoding = 'async';
      img.src = s.image_src;
    });
  }, [slides]);

  const goToPrev = React.useCallback(() => {
    if (!slideCount) return;
    setSlideIndex((prev) => (prev - 1 + slideCount) % slideCount);
  }, [slideCount]);

  const goToNext = React.useCallback(() => {
    if (!slideCount) return;
    setSlideIndex((prev) => (prev + 1) % slideCount);
  }, [slideCount]);

  const openCurrentImage = () => {
    const cur = slides[slideIndex];
    if (!cur) return;
    setImageDialog({ open: true, src: cur.image_src, title: cur.heading || `Slide ${slideIndex + 1}` });
  };

  if (slideCount === 0) return null;

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: 220, sm: 280, md: 340 },
          mb: 2,
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: 2,
          backgroundColor: '#f3e5f5',
          border: '1px solid #e1bee7',
        }}
        onMouseEnter={clearAutoPlay}
        onMouseLeave={startAutoPlay}
      >
        {/* Persistent top-left title overlay */}
        <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2, color: '#4a148c' }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, letterSpacing: 0.3, fontSize: { xs: '1.25rem', sm: '1.35rem', md: '1.5rem' } }}
          >
            Kotilinga Kshetram
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.95 }}>
            {DEFAULT_SUBTITLE}
          </Typography>
        </Box>

        {/* Bottom-right QR code panel */}
        <Box
          sx={{
            position: 'absolute',
            right: 8,
            bottom: 8,
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.75,
          }}
        >
          <Typography variant="caption" sx={{ color: '#4a148c', fontWeight: 700, lineHeight: 1.2 }}>
            For more details
          </Typography>
          <Box
            component="img"
            src={QR_IMAGE_SRC}
            alt="QR code to Kotilinga details"
            sx={{ width: 78, height: 78, borderRadius: 1.5, background: 'white', p: 0.5, pointerEvents: 'none' }}
          />
          <Typography variant="caption" sx={{ color: '#4a148c', opacity: 0.9 }}>
            icctmemphis.org/kotilinga
          </Typography>
        </Box>

        {/* Slides */}
        {slides.map((s, idx) => (
          <Box
            key={`slide-${idx}`}
            aria-hidden={slideIndex !== idx}
            sx={{
              position: 'absolute',
              inset: 0,
              opacity: slideIndex === idx ? 1 : 0,
              transition: 'opacity 900ms ease-in-out',
              willChange: 'opacity',
              pointerEvents: slideIndex === idx ? 'auto' : 'none',
            }}
          >
            <Box
              component="img"
              src={s.image_src}
              alt={s.heading || `Slide ${idx + 1}`}
              loading="eager"
              decoding="async"
              role="button"
              tabIndex={slideIndex === idx ? 0 : -1}
              onClick={openCurrentImage}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openCurrentImage();
                }
              }}
              sx={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                cursor: 'pointer',
                transition: 'transform 1200ms ease-in-out',
                transform: slideIndex === idx ? 'scale(1.0)' : 'scale(1.005)',
              }}
            />
            {s.heading ? (
              <Box sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, p: { xs: 2, md: 3 }, color: '#4a148c' }}>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, transition: 'opacity 600ms ease-in-out', opacity: slideIndex === idx ? 1 : 0 }}
                >
                  {s.heading}
                </Typography>
              </Box>
            ) : null}
          </Box>
        ))}

        {/* Navigation arrows */}
        <IconButton
          aria-label="Previous slide"
          onClick={() => { goToPrev(); startAutoPlay(); }}
          sx={{
            position: 'absolute',
            top: '50%',
            left: 8,
            transform: 'translateY(-50%)',
            bgcolor: 'rgba(255,255,255,0.85)',
            '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <IconButton
          aria-label="Next slide"
          onClick={() => { goToNext(); startAutoPlay(); }}
          sx={{
            position: 'absolute',
            top: '50%',
            right: 8,
            transform: 'translateY(-50%)',
            bgcolor: 'rgba(255,255,255,0.85)',
            '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
          }}
        >
          <ArrowBackIcon sx={{ transform: 'rotate(180deg)' }} />
        </IconButton>

        {/* Dots */}
        <Box sx={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 1 }}>
          {slides.map((_, i) => (
            <Box
              key={`dot-${i}`}
              onClick={() => { setSlideIndex(i); startAutoPlay(); }}
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                cursor: 'pointer',
                bgcolor: i === slideIndex ? 'white' : 'rgba(255,255,255,0.6)',
                border: '1px solid rgba(0,0,0,0.2)',
              }}
            />
          ))}
        </Box>
      </Box>

      <Dialog
        open={imageDialog.open}
        onClose={() => setImageDialog({ open: false, src: '', title: '' })}
        maxWidth="lg"
      >
        <IconButton
          aria-label="Close image"
          onClick={() => setImageDialog({ open: false, src: '', title: '' })}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: 'rgba(255,255,255,0.9)', '&:hover': { bgcolor: '#fff' } }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0 }}>
          <Box
            component="img"
            src={imageDialog.src}
            alt={imageDialog.title}
            sx={{ display: 'block', width: '100%', height: 'auto', maxHeight: '85vh', objectFit: 'contain' }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
